import { Link } from "react-router-dom";
import {
  Store,
  ExternalLink,
  Phone,
  Sparkles,
  Building2,
  Globe,
  Package,
  Layers3,
  Shirt,
  Wrench,
  Printer,
  BadgeDollarSign,
  Tags,
  Star,
  Image as ImageIcon,
  PlayCircle,
  Youtube,
} from "lucide-react";
import {
  vendors,
  featuredVendors,
  vendorCategoryOrder,
  type Vendor,
  type VendorCategory,
  type FeaturedVendor,
} from "../data/vendors";

const DRIP_APPS_YOUTUBE_CHANNEL =
  "https://youtube.com/@dripapps?si=lWE-H-5-YUQCWX7g";

function normalizeUrl(url?: string) {
  if (!url) return "";
  return url.replace(/^https?:\/\//, "").replace(/\/$/, "");
}

function BluePanel({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        "relative overflow-hidden rounded-[28px] border border-dtf-accent/20",
        "bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.18),transparent_42%),linear-gradient(180deg,rgba(7,16,35,0.98)_0%,rgba(6,14,30,0.98)_100%)]",
        "shadow-[0_0_0_1px_rgba(245,158,11,0.05),0_0_34px_rgba(37,99,235,0.16)]",
        className,
      ].join(" ")}
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.08),transparent_30%,transparent_70%,rgba(37,99,235,0.06))]" />
      <div className="pointer-events-none absolute inset-0 rounded-[28px] ring-1 ring-inset ring-white/5" />
      {children}
    </div>
  );
}

function StatPill({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
      <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-dtf-text-muted">
        <span className="text-dtf-accent">{icon}</span>
        <span>{label}</span>
      </div>
      <div className="text-base font-semibold text-white">{value}</div>
    </div>
  );
}

function getCategoryIcon(category: VendorCategory) {
  switch (category) {
    case "DTF Equipment & General Suppliers":
      return <Store className="h-5 w-5" />;
    case "Parts & Repair":
      return <Wrench className="h-5 w-5" />;
    case "Ink, Film & Consumables":
      return <Package className="h-5 w-5" />;
    case "Printer Manufacturers & Dealer Networks":
      return <Printer className="h-5 w-5" />;
    case "T-Shirt Wholesalers":
      return <Shirt className="h-5 w-5" />;
    case "Transfers, Printing & Decorators":
      return <BadgeDollarSign className="h-5 w-5" />;
    case "Blanks & Specialty Products":
      return <Layers3 className="h-5 w-5" />;
    default:
      return <Store className="h-5 w-5" />;
  }
}

function getFeaturedBadge(vendor: FeaturedVendor, index: number) {
  const lowerName = vendor.name.toLowerCase();

  if (lowerName.includes("creek")) {
    return "Exclusive Maintenance Vendor";
  }

  if (
    lowerName.includes("gang sheet") ||
    lowerName.includes("drip apps") ||
    index === 0
  ) {
    return "Exclusive Gang Sheet Builder";
  }

  return "Featured Industry Vendor";
}

function isCreekVendor(vendor: FeaturedVendor) {
  return vendor.name.toLowerCase().includes("creek");
}

function FeaturedImageSlot({
  title,
  src,
  alt,
  fallbackText,
  imageClassName = "",
}: {
  title: string;
  src?: string;
  alt: string;
  fallbackText: string;
  imageClassName?: string;
}) {
  return (
    <div className="rounded-[26px] border border-white/10 bg-[linear-gradient(180deg,rgba(9,21,44,0.95)_0%,rgba(7,15,31,0.98)_100%)] p-4 sm:p-5">
      <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-dtf-text-muted">
        <ImageIcon className="h-3.5 w-3.5 text-dtf-blue" />
        {title}
      </div>

      <div className="flex min-h-[180px] items-center justify-center rounded-2xl border border-white/8 bg-black/20 p-4">
        {src ? (
          <img
            src={src}
            alt={alt}
            className={[
              "max-h-40 w-auto max-w-full object-contain",
              imageClassName,
            ].join(" ")}
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-center text-dtf-text-muted">
            <ImageIcon className="mb-3 h-10 w-10 text-dtf-blue" />
            <p className="text-sm">{fallbackText}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function FeaturedVendorMedia({
  vendor,
}: {
  vendor: FeaturedVendor;
}) {
  if (isCreekVendor(vendor)) {
    return (
      <div className="grid gap-4">
        <FeaturedImageSlot
          title="Vendor Logo"
          src={vendor.logo}
          alt={`${vendor.name} logo`}
          fallbackText="Add logo file to /public/vendors/creek-logo.png"
        />

        <FeaturedImageSlot
          title="Purple Power"
          src="/vendors/purple-power.png"
          alt="Purple Power product"
          fallbackText="Add Purple Power image to /public/vendors/purple-power.png"
          imageClassName="max-h-48"
        />
      </div>
    );
  }

  return (
    <FeaturedImageSlot
      title="Vendor Logo"
      src={vendor.logo}
      alt={`${vendor.name} logo`}
      fallbackText="Add logo file to /public/vendors/"
    />
  );
}

function FeaturedVendorCard({
  vendor,
  index,
}: {
  vendor: FeaturedVendor;
  index: number;
}) {
  return (
    <BluePanel className="mb-8 border-dtf-blue/30 shadow-[0_0_0_1px_rgba(37,99,235,0.18),0_0_44px_rgba(37,99,235,0.2)]">
      <div className="p-6 sm:p-7 lg:p-8">
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-full border border-dtf-accent/20 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
            <Star className="h-3.5 w-3.5" />
            Featured Vendor
          </div>

          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-dtf-accent">
            {getFeaturedBadge(vendor, index)}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[340px_minmax(0,1fr)]">
          <div className="pt-2">
            <FeaturedVendorMedia vendor={vendor} />
          </div>

          <div className="min-w-0">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-dtf-text-muted">
              <Building2 className="h-3.5 w-3.5 text-dtf-blue" />
              Vendor Spotlight
            </div>

            <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              {vendor.name}
            </h2>

            <p className="mt-3 text-lg font-semibold text-dtf-blue">
              {vendor.title}
            </p>

            <p className="mt-4 max-w-4xl text-base leading-8 text-dtf-text sm:text-lg">
              {vendor.description}
            </p>

            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              {vendor.website && (
                <a
                  href={vendor.website}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 transition hover:border-dtf-blue/30 hover:bg-dtf-blue/10"
                >
                  <div className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-dtf-text-muted">
                    Website
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="truncate text-sm font-medium text-white">
                      {normalizeUrl(vendor.website)}
                    </div>
                    <ExternalLink className="h-4 w-4 shrink-0 text-dtf-accent" />
                  </div>
                </a>
              )}

              {vendor.platform && (
                <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
                  <div className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-dtf-text-muted">
                    Platform
                  </div>
                  <div className="text-sm font-medium text-white">
                    {vendor.platform}
                  </div>
                </div>
              )}

              {vendor.poweredBy && (
                <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
                  <div className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-dtf-text-muted">
                    Powered by
                  </div>
                  <div className="text-sm font-medium text-white">
                    {vendor.poweredBy}
                  </div>
                </div>
              )}
            </div>

            {vendor.tags && vendor.tags.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2">
                {vendor.tags.map((tag) => (
                  <span
                    key={`${vendor.name}-${tag}`}
                    className="rounded-full border border-dtf-blue/20 bg-dtf-blue/10 px-3 py-1 text-xs font-medium text-dtf-text"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {vendor.notes && vendor.notes.trim() !== "" && (
              <div className="mt-5 rounded-2xl border border-white/8 bg-black/18 p-4">
                <div className="mb-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-dtf-blue">
                  <Sparkles className="h-3.5 w-3.5" />
                  Notes
                </div>
                <p className="text-sm leading-7 text-dtf-text">{vendor.notes}</p>
              </div>
            )}

            {vendor.contact && vendor.contact.trim() !== "" && (
              <div className="mt-4 rounded-2xl border border-white/8 bg-black/18 px-4 py-3">
                <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-dtf-blue">
                  <Phone className="h-3.5 w-3.5" />
                  Contact
                </div>
                <p className="text-sm leading-7 text-dtf-text">
                  {vendor.contact}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </BluePanel>
  );
}

function FeaturedVendorTrainingCTA() {
  return (
    <BluePanel className="mb-8 border-dtf-blue/25">
      <div className="p-6 sm:p-7 lg:p-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-3xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-dtf-blue/20 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
              <PlayCircle className="h-3.5 w-3.5" />
              Training Available
            </div>

            <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Gang Sheet Builder Training
            </h2>

            <p className="mt-3 max-w-2xl text-base leading-8 text-dtf-text sm:text-lg">
              Full playlist training for setup, Shopify, WordPress, standalone
              platforms, builder features, and workflow is now organized inside the
              Video Library.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link
              to="/videos"
              className="inline-flex items-center gap-2 rounded-2xl border border-dtf-blue/30 bg-dtf-blue/10 px-5 py-3 text-sm font-semibold text-white transition hover:border-dtf-blue/50 hover:bg-dtf-blue/20"
            >
              <PlayCircle className="h-4 w-4" />
              View Training Videos
              <ExternalLink className="h-4 w-4 text-dtf-accent" />
            </Link>

            <a
              href={DRIP_APPS_YOUTUBE_CHANNEL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-2xl border border-red-500/20 bg-red-500/10 px-5 py-3 text-sm font-semibold text-white transition hover:border-red-500/40 hover:bg-red-500/20"
            >
              <Youtube className="h-4 w-4" />
              Drip Apps YouTube
              <ExternalLink className="h-4 w-4 text-dtf-accent" />
            </a>
          </div>
        </div>
      </div>
    </BluePanel>
  );
}

function VendorCard({
  name,
  website,
  contact,
  notes,
  category,
  tags,
}: Vendor) {
  return (
    <div className="group relative overflow-hidden rounded-[26px] border border-white/10 bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.14),transparent_48%),linear-gradient(180deg,rgba(9,21,44,0.98)_0%,rgba(7,15,31,0.98)_100%)] p-5 transition duration-200 hover:-translate-y-0.5 hover:border-dtf-blue/30 hover:shadow-[0_0_26px_rgba(37,99,235,0.18)]">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.06),transparent_35%,transparent_65%,rgba(37,99,235,0.04))]" />

      <div className="relative">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="inline-flex items-center gap-2 rounded-full border border-dtf-accent/20 bg-black/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-dtf-accent">
              <Building2 className="h-3.5 w-3.5" />
              {category}
            </div>

            <h2 className="mt-3 text-2xl font-bold tracking-tight text-white transition group-hover:text-dtf-blue">
              {name}
            </h2>
          </div>

          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-black/20 text-dtf-blue transition group-hover:border-dtf-blue/30 group-hover:bg-dtf-blue/10">
            <Store className="h-5 w-5" />
          </div>
        </div>

        <div className="space-y-3">
          {website && (
            <a
              href={website}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/18 px-4 py-3 text-sm text-dtf-text transition hover:border-dtf-blue/30 hover:text-white"
            >
              <div className="flex min-w-0 items-center gap-3">
                <Globe className="h-4 w-4 shrink-0 text-dtf-blue" />
                <span className="truncate">{normalizeUrl(website)}</span>
              </div>
              <ExternalLink className="h-4 w-4 shrink-0 text-dtf-accent" />
            </a>
          )}

          {contact && contact.trim() !== "" && (
            <div className="rounded-2xl border border-white/8 bg-black/18 px-4 py-3">
              <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-dtf-blue">
                <Phone className="h-3.5 w-3.5" />
                Contact
              </div>
              <p className="text-sm leading-7 text-dtf-text">{contact}</p>
            </div>
          )}

          {tags && tags.length > 0 && (
            <div className="rounded-2xl border border-white/8 bg-black/18 p-4">
              <div className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-dtf-blue">
                <Tags className="h-3.5 w-3.5" />
                Also Offers
              </div>

              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span
                    key={`${name}-${tag}`}
                    className="rounded-full border border-dtf-blue/20 bg-dtf-blue/10 px-3 py-1 text-xs font-medium text-dtf-text"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {notes && notes.trim() !== "" && (
            <div className="rounded-2xl border border-white/8 bg-black/18 p-4">
              <div className="mb-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-dtf-blue">
                <Sparkles className="h-3.5 w-3.5" />
                Notes
              </div>
              <p className="text-sm leading-7 text-dtf-text">{notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function groupVendorsByCategory(items: Vendor[]) {
  return vendorCategoryOrder
    .map((category) => ({
      category,
      items: items
        .filter((vendor) => vendor.category === category)
        .sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .filter((group) => group.items.length > 0);
}

export default function Vendors() {
  const groupedVendors = groupVendorsByCategory(vendors);
  const featuredCount = featuredVendors.length;
  const totalVendors = vendors.length + featuredCount;
  const totalCategories = groupedVendors.length;
  const wholesalerCount = vendors.filter(
    (vendor) => vendor.category === "T-Shirt Wholesalers"
  ).length;

  const primaryFeaturedVendor = featuredVendors[0];
  const secondaryFeaturedVendors = featuredVendors.slice(1);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <BluePanel className="mb-6">
        <div className="p-6 sm:p-7">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-dtf-accent/20 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
                <Store className="h-3.5 w-3.5" />
                Vendors
              </div>

              <h1 className="mb-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
                Vendor Directory
              </h1>

              <p className="text-lg leading-8 text-dtf-text">
                Keep your favorite DTF suppliers, parts sources, consumable vendors,
                printer manufacturers, transfer providers, blank apparel wholesalers,
                and recommended tools organized in one place for faster ordering and
                easier shop management.
              </p>
            </div>

            <div className="grid gap-3 sm:min-w-[320px] sm:grid-cols-2">
              <StatPill
                icon={<Store className="h-4 w-4" />}
                label="Total Vendors"
                value={`${totalVendors}`}
              />
              <StatPill
                icon={<Layers3 className="h-4 w-4" />}
                label="Categories"
                value={`${totalCategories}`}
              />
              <StatPill
                icon={<Shirt className="h-4 w-4" />}
                label="Wholesalers"
                value={`${wholesalerCount}`}
              />
              <StatPill
                icon={<Star className="h-4 w-4" />}
                label="Featured"
                value={`${featuredCount}`}
              />
            </div>
          </div>
        </div>
      </BluePanel>

      {primaryFeaturedVendor && (
        <FeaturedVendorCard
          vendor={primaryFeaturedVendor}
          index={0}
        />
      )}

      <FeaturedVendorTrainingCTA />

      {secondaryFeaturedVendors.map((vendor, index) => (
        <FeaturedVendorCard
          key={`featured-${vendor.name}`}
          vendor={vendor}
          index={index + 1}
        />
      ))}

      <BluePanel className="mb-6">
        <div className="flex flex-col gap-4 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm text-dtf-text-muted">Showing results</div>
            <div className="text-2xl font-bold text-white">
              {totalVendors} vendor{totalVendors === 1 ? "" : "s"} across{" "}
              {totalCategories} categories
            </div>
          </div>

          <div className="rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-dtf-blue">
            Full directory visible
          </div>
        </div>
      </BluePanel>

      <div className="space-y-8">
        {groupedVendors.map((group) => (
          <section key={group.category}>
            <BluePanel className="mb-4">
              <div className="flex flex-col gap-3 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-black/20 text-dtf-blue">
                    {getCategoryIcon(group.category)}
                  </div>

                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-dtf-text-muted">
                      Category
                    </div>
                    <h2 className="text-2xl font-bold text-white">
                      {group.category}
                    </h2>
                  </div>
                </div>

                <div className="rounded-full border border-dtf-accent/20 bg-black/20 px-4 py-2 text-sm font-semibold text-dtf-accent">
                  {group.items.length} vendor{group.items.length === 1 ? "" : "s"}
                </div>
              </div>
            </BluePanel>

            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {group.items.map((vendor) => (
                <VendorCard
                  key={`${vendor.category}-${vendor.name}`}
                  name={vendor.name}
                  category={vendor.category}
                  tags={vendor.tags}
                  website={vendor.website}
                  contact={vendor.contact}
                  notes={vendor.notes}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}